import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Alert, AlertColor, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import { useData } from '../../contexts/DataContext';
import {
  fetchCompanyMessages,
  dismissCompanyMessage,
  CompanyMessage,
} from '../../services/dataService';

const mapTypeToAlertColor = (type: string): AlertColor => {
  switch (type) {
    case 'notice':
      return 'info';
    case 'warning':
      return 'warning';
    case 'info':
      return 'success';
    default:
      return 'info';
  }
};

const CompanyMessages = () => {
  const { targetCompany } = useData();
  const [messages, setMessages] = useState<CompanyMessage[]>([]);

  const fetchMessages = async () => {
    if (!targetCompany) return;
    // Check if we need to make a new API call
    const lastCallTime = localStorage.getItem('lastCompanyMessagesCall');
    if (lastCallTime) {
      const lastCall = DateTime.fromISO(lastCallTime);
      const now = DateTime.now();
      const minutesSinceLastCall = now.diff(lastCall, 'minutes').minutes;

      if (minutesSinceLastCall < 5) {
        console.info('Skipping Messages API call - less than 5 minutes since last call');
        return;
      }
    }

    try {
      const fetchedMessages = await fetchCompanyMessages(targetCompany);
      localStorage.setItem('lastCompanyMessagesCall', DateTime.now().toISO());
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching company messages:', error);
    }
  };

  useEffect(() => {
    // Fetch messages when component mounts
    fetchMessages();

    const handleFocus = () => {
      fetchMessages();
    };

    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [targetCompany]);

  const handleDismiss = async (messageId: string) => {
    try {
      await dismissCompanyMessage(messageId);
      // Remove the message from the local state
      setMessages(messages.filter((message) => message.id !== messageId));
    } catch (error) {
      console.error('Error dismissing message:', error);
    }
  };

  if (!messages.length) return null;

  return createPortal(
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
      {messages.map((message) => (
        <Alert
          key={message.id}
          severity={mapTypeToAlertColor(message.type)}
          sx={{ borderRadius: 0 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => handleDismiss(message.id)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {message.message}
        </Alert>
      ))}
    </Box>,
    document.body
  );
};

export default CompanyMessages;
